<template>
  <Loading v-if="isLoading" :type="2" height="40rem" />
  <div v-else class="p-4 position-relative">
    <div v-bind:style="{ color: this.getTopTextColor() }">
      <i class="chart line icon" />{{ this.getPercent() }}%
      {{ topText }}
    </div>
    <h3 id="title">{{ title }}</h3>
    <h5 id="subtitle">{{ subtitle }}</h5>
    <canvas
      ref="myCanvas"
      v-bind:id="'graphicCanvas' + this.id"
      class="canvas w-100 h-100"
    >
    </canvas>
    <div
      class="overlay d-flex position-absolute font-weight-light justify-content-center align-items-center text-white h-100 w-100"
      v-if="dataEmpty"
    >
      <div class="text-center">
        <div>
          <i class="icon exclamation circle"></i>
        </div>
        <div>
          {{ "components.charts.noDataOverlay" | i18n }}
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import Loading from "../others/Loading.vue";
import { Chart } from "chart.js";
/**
 * @description Composante qui génère un graphique selon les données envoyées
 * @author Benjamin Caron
 * @author Philippe Ménard
 * @vue-prop {String} title - Le titre du graphique
 * @vue-prop {String} topText - Le texte en haut du titre du graphique
 * @vue-prop {String} subtitle - Le sous-titre du graphique
 * @vue-prop {String} type - Le type de graphique (bar, pie ou line)
 * @vue-prop {Number} id - L'id du graphique
 * @vue-data {CSSStyleDeclaration} documentStyle - Le document de style de l'application
 * @vue-data {Array} colors - Les couleurs possible du graphique
 * @vue-data {Number} colorCounter - Le compteur pour la couleur courante
 * @vue-data {Array} monthNames - Les mois de l'année en français
 * @vue-data {Array} dataBreakdown - Les données du graphique des cas critiques
 * @vue-data {Array} dataNotifications - Les données du graphique des notifications
 * @vue-data {Boolean} refreshDone - Indique si la requête de l'api est finie
 * @vue-data {Boolean} dataEmpty - Si le graphique est vide
 * @vue-data {Boolean} isLoading - Si le chargement des données est en cours
 */
export default {
  components: {
    Loading
  },
  props: {
    title: {
      type: String,
      required: true
    },
    topText: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      required: true
    },
    type: {
      type: String,
      required: true
    },
    id: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      documentStyle: getComputedStyle(document.body),
      colors: ["primary", "secondary", "danger"],
      colorCounter: 0,
      monthNames: [
        "Janvier",
        "Février",
        "Mars",
        "Avril",
        "Mai",
        "Juin",
        "Juillet",
        "Août",
        "Septembre",
        "Octobre",
        "Novembre",
        "Décembre"
      ],
      dataBreakdown: [],
      dataNotifications: [],
      refreshDone: false,
      dataEmpty: false,
      isLoading: true
    };
  },
  methods: {
    /**
     * @description Fonction qui donne la couleur du texte du haut selon le pourcentage
     * @returns {String} La couleur du texte
     */
    getTopTextColor() {
      return this.getPercent() <= 0 ? "green" : "red";
    },
    /**
     * @description Fonction qui donne le pourcentage d'augmentation selon les deux derniers mois
     * @returns {Number} Le pourcentage d'augmentation
     */
    getPercent() {
      let data = this.getData();
      if (data == undefined) {
        return 0;
      }

      let lastMonth = data[data.length - 2];
      let thisMonth = data[data.length - 1];
      if (lastMonth == 0 || thisMonth == 0) {
        return 0;
      }
      let percent = Math.round(((thisMonth - lastMonth) / thisMonth) * 100);
      return isNaN(percent) ? 0 : percent;
    },
    /**
     * @description Fonction qui donne une couleur dans le fichier variable.scss
     * @param {String} color La couleur à aller chercher dans le fichier
     * @returns {String} La couleur en question
     */
    getColor(color) {
      return this.documentStyle.getPropertyValue("--" + color);
    },
    /**
     * @description Fonction qui indique la couleur courante du graphique
     * @returns {String} La couleur courante du graphique
     */
    getCurrentColor() {
      let colorIndex = this.id + this.colorCounter;
      if (colorIndex > 2) {
        colorIndex = 0;
      }
      this.colorCounter++;
      return this.colors[colorIndex];
    },
    /**
     * @description Fonction qui donne la liste de couleurs du graphique
     * @returns {Array} La liste de couleurs
     */
    getArrayOfColors() {
      var numberOfValues = 6;
      var colors = [];
      var currentColor = this.getCurrentColor();
      for (let i = 0; i < numberOfValues; i++) {
        colors.push(this.getColor(currentColor));
      }
      return colors;
    },
    /**
     * @description Fonction qui génère les statistiques du graphique selon l'id envoyé en props
     * @returns {Object} Les données demandées (les notifications ou les pannes)
     */
    generateData() {
      return this.id == 1
        ? this.getBreakdownStats()
        : this.getNotificationStats();
    },
    /**
     * @description Fonction qui retourne les bonnes données selon l'id envoyé en paramètre
     * @returns {Object} Les bonnes données (les notifications ou les pannes)
     */
    getData() {
      return this.id == 1 && this.dataBreakdown
        ? Object.values(this.dataBreakdown).reverse()
        : this.dataNotifications;
    },
    /**
     * @description Fonction qui donne le bon dataset selon les données envoyées
     * @returns {Array} Les datasets pour le graphique
     */
    async generateDatasets() {
      let datasets = [];
      let colors = this.getArrayOfColors();
      let borderColor = colors[0];
      await this.generateData();
      let data = this.getData();
      this.dataEmpty = data.reduce((a, b) => a + b, 0) === 0;
      datasets.push({
        data: data,
        backgroundColor: colors,
        hoverBackgroundColor: colors,
        fill: false,
        borderColor: borderColor
      });
      this.isLoading = false;
      return datasets;
    },
    /**
     * @description Fonction async qui va chercher auprès de l'api les données des dernières pannes
     */
    async getBreakdownStats() {
      this.refreshDone = false;
      await this.$api
        .getBreakdownsLastSixMonths(this.$user.get().client_id)
        .then(res => {
          this.dataBreakdown = res.data;
        })
        .catch()
        .finally(() => {
          this.requestDone = true;
        });
    },
    /**
     * @description Fonction async qui va chercher auprès de l'api les données des dernières notifications
     */
    async getNotificationStats() {
      this.refreshDone = false;
      await this.$api
        .getNotificationsLastSixMonths(this.$user.get().client_id)
        .then(res => {
          this.dataNotifications = res.data.data.reverse();
        })
        .catch()
        .finally(() => {
          this.requestDone = true;
        });
    },
    /**
     * @description Fonction qui donne les id des x derniers mois
     * @param {Number} amount Le nombre de mois à avoir
     * @returns {Array} Les id des derniers mois
     */
    getLastMonthsId(amount) {
      var currentMonth = new Date().getMonth();
      var lastMonthsId = [];
      for (let i = amount; i--; i < 0) {
        let month = currentMonth - i;
        if (month < 0) {
          month = 12 + month;
        }
        lastMonthsId.push(month);
      }
      return lastMonthsId;
    },
    /**
     * @description Fonction qui donne les noms des x derniers mois
     * @param {Number} amount Le nombre de mois à voir
     * @returns {Array} Le nom des derniers mois en texte
     */
    getLastMonths(amount) {
      var lastMonths = [];
      this.getLastMonthsId(amount).forEach(month => {
        lastMonths.push(this.monthNames[month]);
      });
      return lastMonths;
    },
    /**
     * @description Fonction qui génère le graphique dans le canvas
     */
    async generateChart() {
      Chart.defaults.global.legend.display = false;
      let dataset = await this.generateDatasets();
      new Chart(this.$refs.myCanvas, {
        responsive: true,
        type: this.type,
        data: {
          labels: this.getLastMonths(6),
          datasets: dataset
        },
        options: {
          scales: {
            yAxes: [
              {
                ticks: {
                  beginAtZero: true,
                  fontColor: "#B4B4B4"
                }
              }
            ],
            xAxes: [
              {
                ticks: {
                  fontColor: "#B4B4B4"
                }
              }
            ]
          }
        }
      });
    }
  },
  /**
   * @description Permet d'obtenir les données nécessaires au graphique
   */
  beforeMount() {
    this.generateData();
    this.generateDatasets();
  },
  /**
   * @description Génère le graphique lorsque la page se charge
   */
  mounted() {
    this.generateChart();
  }
};
</script>
<style lang="scss" scoped>
#subtitle {
  color: $gray-400;
  font-weight: 300;
}
#title {
  font-weight: 400;
  color: $gray-800;
}
.overlay {
  border-radius: $border-radius-lg;
  font-size: 1.25em;
  background: rgba(0, 0, 0, 0.35);
  top: 0;
  left: 0;
}
</style>
